import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { authAPI, saveTokens, clearTokens } from '../services/api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'divispend_access_token';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await authAPI.getMe();
      setUser(data);
      setIsAuthenticated(true);
    } catch (err) {
      console.warn('Session check failed — clearing stored tokens:', err?.response?.status || err);
      setUser(null);
      setIsAuthenticated(false);
      clearTokens();
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = useCallback(async (email, password, totpCode, backupCode, captchaToken) => {
    const payload = { email, password };
    if (totpCode) payload.totp_code = totpCode;
    if (backupCode) payload.backup_code = backupCode;
    if (captchaToken) payload.captcha_token = captchaToken;
    const { data } = await authAPI.login(payload);
    if (data.requires_2fa) {
      return data;
    }
    if (data.reset_required) {
      return data;
    }
    if (data.access_token) saveTokens(data.access_token, data.refresh_token);
    setUser(data);
    setIsAuthenticated(true);
    return data;
  }, []);

  const register = useCallback(async (formData) => {
    const { data } = await authAPI.register(formData);
    if (data.access_token) saveTokens(data.access_token, data.refresh_token);
    setUser(data);
    setIsAuthenticated(true);
    return data;
  }, []);

  const googleLogin = useCallback(async (sessionId) => {
    const { data } = await authAPI.googleSession(sessionId);
    if (data.access_token) saveTokens(data.access_token, data.refresh_token);
    setUser(data);
    setIsAuthenticated(true);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await authAPI.logout(); } catch (err) { console.warn('Logout API call failed (tokens cleared locally):', err); }
    clearTokens();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await authAPI.getMe();
      setUser(data);
    } catch (err) { console.error('Failed to refresh user:', err); }
  }, []);

  const value = useMemo(() => ({
    user, loading, isAuthenticated, login, register, googleLogin, logout, refreshUser, checkAuth
  }), [user, loading, isAuthenticated, login, register, googleLogin, logout, refreshUser, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
